import { LegalEntityEnum, MatrixType, OrderType } from '@onrunning/backstage-models';
import { computed, nextTick, ref, type ComputedRef, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useNewRelic } from '@/composables/useNewRelic';
import { useNotification } from '@/composables/useNotification';
import { useExcelUploadValidation } from '@/features/excel-order-upload/composables/useExcelUploadValidation';
import { ExcelUploadOutcome } from '@/features/excel-order-upload/enums/excel-upload.enums';
import type {
  ExcelUploadGroupedResult,
  FailedRow,
} from '@/features/excel-order-upload/types/excel-template-upload.types';
import { CrudService } from '@/services/crud.service';
import { FeaturesService } from '@/services/features.service';
import { ProductsMatrixService } from '@/services/products-matrix.service';
import { useOrderStore } from '@/stores/order';
import { useProductMatrixStore } from '@/stores/product-matrix';
import { useProductPricesStore } from '@/stores/product-prices';
import { useUserStore } from '@/stores/user';
import type { ProductMatrixApiResponse } from '@/types/matrix.interface';
import type { MatrixOrderProduct } from '@/types/product.interface';
import { today, toISOString } from '@/utils/temporalUtils';
import { storeToRefs } from 'pinia';
import {
  recordGenerationError,
  recordGenerationMetrics,
  recordParsingMetrics,
  recordUploadError,
  recordValidationMetrics,
} from '../utils/excel-observability';

export function useExcelTemplate(
  productMatrix: Ref<ProductMatrixApiResponse | null>,
  defaultSize: ComputedRef<string>,
  matrixType: ComputedRef<MatrixType>,
  loading: Ref<boolean>,
  error: Ref<boolean>,
) {
  const productsMatrixService = new ProductsMatrixService();

  const { t, tm } = useI18n();
  const { notifyError } = useNotification();
  const { noticeError } = useNewRelic();

  const orderStore = useOrderStore();
  const { orderType, orderTypeInfo } = storeToRefs(orderStore);
  const userStore = useUserStore();
  const { productGroupItemPrice } = storeToRefs(useProductPricesStore());
  const productMatrixStore = useProductMatrixStore();

  const router = useRouter();
  const { validateOwnership, validateRows, groupByDateAndPO } = useExcelUploadValidation(matrixType);

  // ---------------------------------------------------------------------------
  // Download state & handler
  // ---------------------------------------------------------------------------

  const downloadingTemplate = ref(false);

  async function onDownloadTemplate(): Promise<void> {
    const generationStart = performance.now();

    try {
      downloadingTemplate.value = true;

      const isDistributor = userStore.accountIsDistributor();

      const seasonStartDate = orderTypeInfo.value?.startDate ?? '';
      const seasonEndDate = orderTypeInfo.value?.endDate;

      if (!seasonStartDate) {
        notifyError(t('excel_order_upload.excel_upload_modal.error.download_template_error'));
        noticeError(new Error('Missing season start date for template download'), {
          feature: 'ProductMatrixTemplateDownload',
        });

        return;
      }

      // Build wholesale prices map from product prices store
      let prices: Record<string, number> | undefined;
      let currency: string | undefined;
      const priceMap = productGroupItemPrice.value;
      if (priceMap && Object.keys(priceMap).length > 0 && !isDistributor) {
        prices = {};
        for (const [productGroupNumber, entry] of Object.entries(priceMap)) {
          if (entry.wholesale != null) {
            prices[productGroupNumber] = entry.wholesale;
          }
        }
        currency = Object.values(priceMap)[0]?.currency;
      }

      // Fetch HMAC signature from the server (lightweight GET, ~50 bytes)
      const signature = await productsMatrixService.getTemplateSignature(userStore.selectedAccount.id);

      // Lazy-load the client-side Excel generator to keep the main bundle small
      const { generateTemplateBlob } = await import(
        '@/features/excel-order-upload/services/excel-template-generate.service'
      );

      // Build translated labels for the current locale
      const translations = tm('matrix.excel_template') as Record<string, string>;

      const blob = await generateTemplateBlob({
        accountId: userStore.selectedAccount.id,
        signature,
        products: productMatrix.value?.products ?? [],
        sizeGroups: productMatrix.value?.sizeGroups ?? [],
        seasonStartDate,
        seasonEndDate,
        prices,
        currency,
        sizeSystem: defaultSize.value,
        locale: userStore.userLocale,
        matrixType: matrixType.value,
        isDistributor,
        legalEntity: userStore.selectedAccount.legalEntity as LegalEntityEnum,
        translations,
      });

      // Trigger browser download
      const filename = `order-template-${userStore.selectedAccount.id}-${new Date().toISOString().split('T')[0]}.xlsx`;
      CrudService.triggerDownload(blob, filename);

      recordGenerationMetrics({
        durationMs: Math.round(performance.now() - generationStart),
        accountId: userStore.selectedAccount.id,
        accountName: userStore.selectedAccount.name,
        legalEntity: userStore.selectedAccount.legalEntity,
        productCount: productMatrix.value?.products?.length ?? 0,
      });
    } catch (err) {
      notifyError(t('excel_order_upload.excel_upload_modal.error.download_template_error'));
      const error = err instanceof Error ? err : new Error(String(err));
      recordGenerationError({
        error,
        accountId: userStore.selectedAccount.id,
        accountName: userStore.selectedAccount.name,
        legalEntity: userStore.selectedAccount.legalEntity,
      });
    } finally {
      downloadingTemplate.value = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Upload state & handlers
  // ---------------------------------------------------------------------------

  const uploadingTemplate = ref(false);
  const uploadFailedRows = ref<FailedRow[]>([]);
  const uploadSuccessCount = ref(0);
  const uploadTotalCount = ref(0);
  const uploadErrorReason = ref<string | null>(null);
  const pendingUploadResult = ref<ExcelUploadGroupedResult | null>(null);
  const switchedToMultiplePreOrder = ref(false);

  const showUploadButton = computed(
    () =>
      !loading.value &&
      !error.value &&
      FeaturesService.orderTemplateDownloadEnabled &&
      orderType.value !== null &&
      [OrderType.PreOrder, OrderType.MultiplePreOrder].includes(orderType.value),
  );

  async function onUploadTemplate(file: File): Promise<ExcelUploadOutcome> {
    try {
      uploadingTemplate.value = true;

      // 1. Parse the uploaded Excel file

      const parseStart = performance.now();
      const { parseUploadedTemplate } = await import(
        '@/features/excel-order-upload/services/excel-template-upload.service'
      );

      // Derive hasPrices from the same condition used during template generation
      // so the parser can determine the column layout without relying on header text.
      const isDistributor = userStore.accountIsDistributor();
      const priceMap = productGroupItemPrice.value;
      const hasPrices = !!priceMap && Object.keys(priceMap).length > 0 && !isDistributor;

      const parsed = await parseUploadedTemplate(file, { hasPrices });
      const parseDurationMs = Math.round(performance.now() - parseStart);

      recordParsingMetrics({
        durationMs: parseDurationMs,
        fileSizeBytes: file.size,
        rowCount: parsed.rows.length,
        accountId: userStore.selectedAccount.id,
        accountName: userStore.selectedAccount.name,
        legalEntity: userStore.selectedAccount.legalEntity,
      });

      // 2. Validate ownership (account + HMAC)
      const ownership = await validateOwnership(parsed.metadata);
      if (!ownership.valid) {
        uploadErrorReason.value = ownership.reason ?? 'ownership_failed';

        return ExcelUploadOutcome.UPLOAD_FAILED;
      }

      // 3. Run row-level validations
      if (!productMatrix.value) {
        uploadErrorReason.value = 'no_products_available';

        return ExcelUploadOutcome.UPLOAD_FAILED;
      }

      const seasonStartDate = orderTypeInfo.value?.startDate ?? '';
      const seasonEndDate = orderTypeInfo.value?.endDate;

      const result = validateRows(parsed.rows, productMatrix.value, seasonStartDate, seasonEndDate);

      // 4. Record validation metrics
      recordValidationMetrics({
        totalRows: parsed.rows.length,
        failedRows: result.failed.length,
        accountId: userStore.selectedAccount.id,
        accountName: userStore.selectedAccount.name,
        legalEntity: userStore.selectedAccount.legalEntity,
      });

      // 5. Group successful rows by (date, PO)
      const grouped = groupByDateAndPO(result, seasonStartDate);

      uploadTotalCount.value = parsed.rows.length;
      uploadSuccessCount.value = result.successful.length;
      uploadFailedRows.value = grouped.failed;

      if (grouped.failed.length > 0) {
        // Show dialog to let user decide
        pendingUploadResult.value = grouped;

        return ExcelUploadOutcome.PARTIAL_SUCCESS;
      } else {
        if (grouped.orders.length === 0) {
          uploadErrorReason.value = 'upload_no_valid_rows';

          return ExcelUploadOutcome.UPLOAD_FAILED;
        }

        // All rows valid — apply directly
        uploadErrorReason.value = null;
        applyUploadedQuantities(grouped);

        return ExcelUploadOutcome.UPLOAD_SUCCESS;
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      uploadErrorReason.value = 'upload_template_error';
      recordUploadError({
        error,
        accountId: userStore.selectedAccount.id,
        accountName: userStore.selectedAccount.name,
        legalEntity: userStore.selectedAccount.legalEntity,
        fileSizeBytes: file?.size,
        context: error.message,
      });

      return ExcelUploadOutcome.UPLOAD_FAILED;
    } finally {
      uploadingTemplate.value = false;
    }
  }

  function applyUploadedQuantities(grouped: ExcelUploadGroupedResult): void {
    // Auto-switch to Multiple Pre-Order when the Excel contains multiple (RRD + PO) groups
    const shouldSwitchToMultiple =
      grouped.orders.length > 1 && orderType.value === OrderType.PreOrder && orderTypeInfo.value;

    if (shouldSwitchToMultiple) {
      orderStore.setOrderTypeInfo(orderTypeInfo.value!, OrderType.MultiplePreOrder);
      switchedToMultiplePreOrder.value = true;
    }

    // Clear existing matrix orders and replace with upload data
    productMatrixStore.setMatrixOrders([]);

    for (const orderGroup of grouped.orders) {
      const matrixProducts: Record<string, MatrixOrderProduct> = {};

      for (const row of orderGroup.products) {
        for (const product of row.matchedProducts) {
          const qty = row.quantities[product.sizeId];
          if (qty > 0) {
            matrixProducts[product.productId] = {
              productName: product.productName,
              productGroupId: product.productGroupId,
              productGroupName: product.productGroupName,
              productGroupNumber: product.productGroupNumber,
              quantity: qty,
              productId: product.productId,
            };
          }
        }
      }

      const total = Object.values(matrixProducts).reduce((sum, p) => sum + p.quantity, 0);

      productMatrixStore.setMatrixOrder({
        date: orderGroup.date,
        matrixProducts,
        total,
        customerPoNumber: orderGroup.poNumber || undefined,
      });
    }

    productMatrixStore.setActiveMatrixOrderIndex(0);
    productMatrixStore.setUpdateStatus(true);

    // Update the route AFTER all store mutations; defer via nextTick so that
    // reactive watchers (e.g. updateUrlDate) triggered by the mutations above
    // have already fired and won't overwrite this navigation.
    if (shouldSwitchToMultiple) {
      nextTick(() => {
        const { name, params } = router.currentRoute.value;
        if (name) {
          const firstDate = productMatrixStore.matrixOrders[0]?.date;
          router.replace({
            name: name as string,
            params: {
              ...params,
              orderType: OrderType.MultiplePreOrder.toLocaleLowerCase(),
              ...(firstDate ? { startDate: toISOString(firstDate) } : {}),
            },
          });
        }
      });
    }
  }

  async function onDownloadFailedReport(): Promise<void> {
    if (!pendingUploadResult.value || pendingUploadResult.value.failed.length === 0) return;

    try {
      const { generateFailedItemsReport } = await import(
        '@/features/excel-order-upload/services/excel-template-upload.service'
      );
      const blob = await generateFailedItemsReport(pendingUploadResult.value.failed);
      CrudService.triggerDownload(blob, `upload-failed-items-${toISOString(today())}.xlsx`);
    } catch {
      notifyError(t('excel_order_upload.upload_error_modal.description.upload_template_error'));
    }
  }

  async function onUploadContinue(): Promise<ExcelUploadOutcome> {
    if (pendingUploadResult.value && pendingUploadResult.value.orders.length > 0) {
      applyUploadedQuantities(pendingUploadResult.value);
      pendingUploadResult.value = null;

      return ExcelUploadOutcome.UPLOAD_SUCCESS;
    } else {
      uploadErrorReason.value = 'upload_no_valid_rows';
      pendingUploadResult.value = null;

      return ExcelUploadOutcome.UPLOAD_FAILED;
    }
  }

  function onUploadAbort(): void {
    pendingUploadResult.value = null;
    uploadFailedRows.value = [];
  }

  return {
    // Download
    downloadingTemplate,
    onDownloadTemplate,
    // Upload
    uploadingTemplate,
    showUploadButton,
    uploadFailedRows,
    uploadSuccessCount,
    uploadTotalCount,
    uploadErrorReason,
    switchedToMultiplePreOrder,
    onUploadTemplate,
    onDownloadFailedReport,
    onUploadContinue,
    onUploadAbort,
  };
}
